import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { navigate } from '../router.ts';
import { get, post } from '../utils/http.ts';
import { Activity, Programme } from '../types.ts';
import { getCurrentUser } from '../auth/session.ts';
import initialisePuzzleMetadata from '../puzzles/puzzleMetadata.ts';
import { getPuzzles } from '../puzzles/puzzleBase.ts';
import { sameOptions } from '../puzzles/preset.ts';
import type { Options } from '../puzzles/wasmBridge/viewModel';

@customElement('app-programme-builder')
export class PuzzleProgrammeBuilder extends LitPage {
  @state() puzzles: Map<string, { name: string; id: number }[]> = new Map();
  private defaultBoards: Map<string, { label: string; options: Options[] }[]> = new Map();

  @state() private activities: (Activity & { _id: string })[] = [];
  @state() private participantName = ''
  @state() private participantEmail = '';
  @state() private participantEmailValid = true;

  @state() isNew: boolean = false;

  styles() {
    return css`
      :host {
        display: block;
        min-height: 100vh;
        padding-bottom: 4rem;
      }
  
      .rows {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
  
      .empty-state {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        color: #888;
      }
  
      .row-card {
        display: flex;
        flex-direction: column;
        transition:
          transform 150ms ease,
          opacity 150ms ease,
          max-height 150ms ease,
          margin 150ms ease,
          padding 150ms ease;
        overflow: hidden;
      }
  
      .row-card--enter {
        opacity: 0;
        transform: translateY(10px);
      }
  
      .row-card--enter.row-card--enter-active {
        opacity: 1;
        transform: translateY(0);
      }
  
      .row-card--exit {
        opacity: 1;
        max-height: 200px;
      }
  
      .row-card--exit.row-card--exit-active {
        opacity: 0;
        transform: translateY(-10px);
        max-height: 0;
        margin-top: 0;
        margin-bottom: 0;
        padding-top: 0;
        padding-bottom: 0;
      }
  
      @media (min-width: 640px) {
        .row-body {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          justify-content: space-between;
        }
      }

      .meta-row {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      @media (min-width: 640px) {
        .meta-row {
          flex-direction: row;
          align-items: flex-end;
          gap: 1rem;
        }

        .meta-field {
          flex: 1 1 0;
        }
      }

      .selects {
        display: flex;
        gap: 0.5rem;
        flex: 1 1 auto;
      }
  
      .buttons {
        display: flex;
        gap: 0.25rem;
        margin-top: 0.5rem;
      }
  
      @media (min-width: 640px) {
        .buttons {
          margin-top: 0;
        }
      }

      .activities-container {
        border: 1px solid #e5e5e5;
      }
  
      .actions-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1rem;
      }
  
      .actions-left {
        display: flex;
        gap: 0.5rem;
      }
  
      .actions-bottom {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 900;
        background: #fff;
        border-top: 1px solid #e5e5e5;
        padding: 0.5rem 1rem;
      }
  
      .actions-bottom-inner {
        max-width: 640px;
        margin: 0 auto;
        display: flex;
        justify-content: flex-start;
        gap: 0.5rem;
      }
  
      .add-fab {
        display: flex;
        justify-content: flex-end;
      }
  
      .add-fab-button {
        border-radius: 50%;
        width: 3rem;
        height: 3rem;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        z-index: 10000;
      }
  
      .buttons button:disabled {
        opacity: 0.35;
        cursor: default;
        pointer-events: none;
        background-color: #f8f8f8;
        border-color: #e5e5e5;
      }
  
      .email-error {
        color: #e53935;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      .input-error {
        border-color: #e53935;
      }
    `;
  }

  async onBeforeEnter(location: { params: { participantEmail?: string } }) {
    await initialisePuzzleMetadata(this.puzzles);
    await this.loadDefaultBoards();

    if (!location.params.participantEmail || location.params.participantEmail === 'new') {
      this.isNew = true;
      return
    }

    this.isNew = false;
    this.participantEmail = location.params.participantEmail;

    const response = await get<Programme>(`carer/${getCurrentUser().emails[0].email}/programme/${this.participantEmail}`);
    if (response.error) {
      console.error(response.error);

      navigate('/programmes');
      return;
    }

    this.participantName = response.result.participantName;
    this.activities = response.result.activities as (Activity & { _id: string })[];

    for (let i = 0; i < this.activities.length; i++) {
      this.activities[i]._id = crypto.randomUUID();
    }
  }

  private async loadDefaultBoards(): Promise<void> {
    const models = await getPuzzles();
    for (const model of models) {
      if (!model.hasDefaultBoards) {
        continue;
      }

      const boards = Array.from(model.difficultyLevels.entries()).map(([label, options]) => ({
        label: model.displayLabel(label),
        options,
      }));
      this.defaultBoards.set(model.name.toLowerCase(), boards);
    }
  }

  private defaultBoardsFor(puzzle: string): { label: string; options: Options[] }[] {
    return this.defaultBoards.get(puzzle) ?? [];
  }

  private getRowElements(): HTMLDivElement[] {
    return Array.from(
      this.renderRoot.querySelectorAll<HTMLDivElement>('[data-row-id]')
    );
  }

  private getPositions(): Map<string, DOMRect> {
    const map = new Map<string, DOMRect>();
    for (const el of this.getRowElements()) {
      const id = el.dataset.rowId!;
      map.set(id, el.getBoundingClientRect());
    }
    return map;
  }

  private playReorderAnimation(prev: Map<string, DOMRect>) {
    const current = this.getPositions();
    for (const el of this.getRowElements()) {
      const id = el.dataset.rowId!;
      const prevBox = prev.get(id);
      const currBox = current.get(id);
      if (!prevBox || !currBox) continue;

      const deltaY = prevBox.top - currBox.top;
      const deltaX = prevBox.left - currBox.left;
      if (deltaX === 0 && deltaY === 0) continue;

      el.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      el.style.transition = 'none';

      requestAnimationFrame(() => {
        el.style.transform = '';
        el.style.transition = 'transform 150ms ease';
      });
    }
  }

  private async moveRow(id: string, direction: -1 | 1) {
    const idx = this.activities.findIndex(r => r._id === id);
    if (idx < 0) {
      return;
    }

    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx>= this.activities.length) {
      return;
    }

    const prevPositions = this.getPositions();

    const arr = [...this.activities];
    const [row] = arr.splice(idx, 1);
    arr.splice(newIdx, 0, row);
    this.activities = arr;

    await this.updateComplete;
    this.playReorderAnimation(prevPositions);
  }

  private async addRow() {
    const first = Array.from(this.puzzles.entries())[0];
    const boards = this.defaultBoardsFor(first[0]);
    const id = crypto.randomUUID();

    this.activities = [
      ...this.activities,
      boards.length > 0
        ? { _id: id, puzzle: first[0], level: -1, options: boards[0].options, label: boards[0].label, completed: false }
        : { _id: id, puzzle: first[0], level: first[1][0].id, completed: false },
    ];

    await this.updateComplete;

    const el = this.renderRoot.querySelector<HTMLElement>(`[data-row-id="${id}"]`);
    if (!el) {
      return;
    }

    el.classList.add('row-card--enter');

    requestAnimationFrame(() => {
      el.classList.add('row-card--enter-active');
      el.addEventListener(
        'transitionend',
        () => {
          el.classList.remove('row-card--enter', 'row-card--enter-active');
        },
        { once: true }
      );
    });

    window.scrollTo({
      top: document.documentElement.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
  }


  private removeRow(id: string) {
    this.activities = this.activities.filter(r => r._id !== id);
  }

  private updatePuzzle(id: string, puzzle: string) {
    const boards = this.defaultBoardsFor(puzzle);
    this.activities = this.activities.map(r =>
      r._id === id
        ? boards.length > 0
          ? { ...r, puzzle: puzzle, level: -1, options: boards[0].options, label: boards[0].label }
          : {
              ...r,
              puzzle: puzzle,
              level: Array.from(this.puzzles.entries()).find(p => p[0] === puzzle)[1][0].id,
              options: undefined,
              label: undefined,
            }
        : r
    );
  }

  private updateLevel(id: string, value: string) {
    this.activities = this.activities.map(r => {
      if (r._id !== id) return r;

      if (value.startsWith('preset:')) {
        return { ...r, level: parseInt(value.slice('preset:'.length), 10), options: undefined, label: undefined };
      }

      const board = this.defaultBoardsFor(r.puzzle)[parseInt(value.slice('board:'.length), 10)];
      return { ...r, level: -1, options: board?.options, label: board?.label };
    });
  }

  private puzzleById(id: string) {
    return Array.from(this.puzzles.entries()).find(p => p[0] === id)!;
  }

  private renderRow(row: Activity & { _id: string }, index: number) {
    const puzzle = this.puzzleById(row.puzzle);
    const boards = this.defaultBoardsFor(row.puzzle);
    const selectedValue = row.options?.length
      ? `board:${boards.findIndex(b => sameOptions(b.options, row.options))}`
      : `preset:${row.level}`;
    const enginePresets =
      boards.length > 0
        ? puzzle[1].filter(l => !row.options?.length && l.id === row.level)
        : puzzle[1];
    const isFirst = index === 0;
    const isLast = index === this.activities.length - 1;
    const prevCompleted = !isFirst && this.activities[index - 1].completed;

    return html`
      <div
        class="row-card"
        data-row-id=${row._id}>
        <div class="row-body">
          <div class="selects">
            <select
              
              .value=${puzzle[0]}
              ?disabled=${row.completed}
              @change=${(e: Event) => {
                const target = e.target as HTMLSelectElement;
                this.updatePuzzle(row._id, target.value);
              }}>
              ${Array.from(this.puzzles.entries()).map(p => html`
                <option value=${p[0]} ?selected=${p[0] === puzzle[0]}>
                  ${p[0].slice(0, 1).toUpperCase() + p[0].slice(1)}
                </option>
              `)}
            </select>

            <select
              
              .value=${selectedValue}
              ?disabled=${row.completed}
              @change=${(e: Event) => {
                const target = e.target as HTMLSelectElement;
                this.updateLevel(row._id, target.value);
              }}>
              ${enginePresets.map(l => html`
                <option value=${`preset:${l.id}`} ?selected=${`preset:${l.id}` === selectedValue}>
                  ${l.name}
                </option>`)}
              ${boards.map((b, idx) => html`
                <option value=${`board:${idx}`} ?selected=${`board:${idx}` === selectedValue}>
                  ${b.label}
                </option>`)}
            </select>
          </div>

          <div class="buttons">
            <button
              type="button"
              title="Move up"
              @click=${() => this.moveRow(row._id, -1)}
              ?disabled=${isFirst || row.completed || prevCompleted}
              style=${row.completed ? 'opacity: 0' : ''}>↑</button>
            <button
              type="button"
              title="Move down"
              @click=${() => this.moveRow(row._id, 1)}
              ?disabled=${isLast || row.completed}
              style=${row.completed ? 'opacity: 0' : ''}>↓</button>
            <button
              type="button"
              title="Delete"
              @click=${() => this.removeRow(row._id)}
              ?disabled=${row.completed}
              style=${row.completed ? 'color: green; opacity: 1; background: unset' : ''}>${row.completed ? '✓' : '✕'}</button>
          </div>
        </div>
      </div>
    `;
  }

  private onEmailInput(e: Event) {
    const target = e.target as HTMLInputElement;
    this.participantEmail = target.value;
    this.participantEmailValid =
      this.participantEmail === '' ||
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.participantEmail);
  }

  private async onSave() {
    if (!this.participantEmail || !this.participantEmailValid) {
      this.participantEmailValid = false;
      return;
    }

    const response = await post(`carer/${getCurrentUser().emails[0].email}/programme/${this.participantEmail}`, {
      carerEmail: getCurrentUser().emails[0].email,
      participantEmail: this.participantEmail,
      participantName: this.participantName,
      activities: this.activities
    } as Programme);

    if (response.error) {
      return;
    }

    navigate('/programmes');
  }

  private onCancel() {
    navigate('/programmes');
  }

  content() {
    return html`
      <div>
        <button 
                type="button"
                @click=${() => navigate('/programmes')}>
          <span></span>
          <span>Back</span>
        </button>
        <h1>${this.isNew ? 'New' : 'Edit'} programme</h1>

        <h3>Participant details</h3>

        <div class="meta-row">
          <div class="meta-field">
            <label for="programme-name">
              Full name
            </label>
            <div>
              <input
                id="participant-name"
                
                type="text"
                .value=${this.participantName}
                @input=${(e: Event) => {
                  const target = e.target as HTMLInputElement;
                  this.participantName = target.value;
                }}
                placeholder="John Smith"
              />
            </div>
          </div>

          <div class="meta-field">
            <label for="participant-email">
              Email address
            </label>
            <div>
              <input
                id="participant-email"
                class="${this.participantEmailValid ? '' : 'input-error'}"
                type="email"
                .value=${this.participantEmail}
                @input=${this.onEmailInput}
                placeholder="john@smith.com"
                required
              />
            </div>
            ${!this.participantEmailValid
              ? html`<div class="email-error">Please enter a valid email address.</div>`
              : null}
          </div>
        </div>

        <h3>Activities</h3>

        <div class="activities-container rows">
          ${this.activities.length === 0
            ? html`
              <div class="empty-state">
                <div>
                  <div>No activities in this programme yet.</div>
                  <div>Add activities using the button below.</div>
                </div>
              </div>
            `
            : this.activities.map((row, i) => this.renderRow(row, i))}
        </div>

        <div class="actions-bar">
          <div class="actions-left"></div>
          <div class="add-fab">
            <button
              class="add-fab-button"
              type="button"
              @click=${this.addRow}
              title="Add puzzle"></button>
          </div>
        </div>

        <div class="actions-bottom">
          <button
            
            type="button"
            @click=${this.onCancel}>
            Cancel
          </button>
          <button
            
            type="button"
            @click=${this.onSave}
            ?disabled=${!this.participantEmail || !this.participantEmailValid}>
            Save
          </button>
        </div>
      </div>
    `;
  }
}
